import React, { useState } from 'react';

import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors, HStack, Primitive, Text, VStack } from '@rtkwlf/fenrir-react';
import { faChevronDown } from '@rtkwlf/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@rtkwlf/pro-solid-svg-icons/faChevronUp';
import type { Ticket } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { LogoBadge } from '@features/tickets/components/logo-badge/logo-badge';
import { TicketType } from '@features/tickets/components/ticket/ticket-type';
import { DateTime } from 'ae-components/dateTime/DateTime';

import { DetailsTextRow } from '../../detailsTextRow/DetailsTextRow';
import { DetailsBadgeRow } from '../../TicketDetails';

export type TicketInfoAccordionProps = {
  ticket: Ticket;
};

export const TicketInfoAccordion: React.FC<TicketInfoAccordionProps> = ({
  ticket,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const chevronIcon = isExpanded ? faChevronUp : faChevronDown;

  return (
    <Primitive.div
      data-testid="ticketInfoAccordion"
      width="full"
      borderColor="neutralDefault"
      borderWidth="default"
      borderStyle="solid"
    >
      <HStack
        xAlign="between"
        yAlign="center"
        backgroundColor="infoHighlight"
        borderStyle="solid"
        borderColor="neutralDefault"
        borderWidth="zero"
        borderBottomWidth={isExpanded ? 'default' : 'zero'}
        paddingX="default"
        paddingY="small"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Text styledAs="body4" textColor="brandDefault">
          {t('ticket:accordion.ticketInfo')}
        </Text>
        <FontAwesomeIcon
          color={`rgb(${colors.text.brand.default})`}
          icon={chevronIcon}
          size="sm"
        />
      </HStack>
      {isExpanded && (
        <VStack data-testid="expanded" padding="default" gap="default">
          <HStack yAlign="top" flexWrap="wrap">
            <DetailsBadgeRow
              titleKey="ticket:details.status"
              value={ticket.status}
            />
            <VStack
              css={css({
                width: 'fit-content',
              })}
            >
              <Text styledAs="body4" whiteSpace="nowrap">
                {t('ticket:details.ticketType')}
              </Text>
              <TicketType value={ticket.type} />
            </VStack>
            <DetailsTextRow
              title={t('ticket:details.created')}
              value={<DateTime value={ticket.createdAt} />}
            />
            <DetailsTextRow
              title={t('ticket:details.lastUpdated')}
              value={<DateTime value={ticket.updatedAt} />}
            />
            <VStack
              css={css({
                rowGap: '0.55rem',
                width: 'fit-content',
              })}
            >
              <Text styledAs="body4" whiteSpace="nowrap">
                {t('ticket:accordion.requestor')}
              </Text>
              <LogoBadge
                text={ticket.requester}
                showLogo={ticket.requesterAgent}
              />
            </VStack>
          </HStack>
        </VStack>
      )}
    </Primitive.div>
  );
};
